import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Alert, CircularProgress, Stack, Typography } from '@mui/material';
import { groupsApi } from '../../services/groups-api-client';
import { seatingApi } from '../../services/seating-api-client';
import { seatingErrorMessage } from '../../lib/seatingErrors';
import { resolveTripTimeZone } from '../../lib/tripTime';
import SeatingChartEditor from './seating/SeatingChartEditor';
import SeatingChartList from './seating/SeatingChartList';
import type { CreateSeatingChartRequest, SeatingChartSummary } from '../../types/seating';
import type { GroupDetail, GroupDinnerHold } from '../../types/groups';

import { seatingChartsKey as CHARTS_KEY } from '../../lib/groupQueryKeys';

/**
 * Seating: the room, and who sits in it.
 *
 * Two screens, one tab. The list is where charts are made and picked; opening
 * one hands the whole tab to the editor, because arranging a room wants the
 * width and a host is only ever seating one dinner at a time.
 *
 * Charts are their own resource, not part of the group payload — the bulk read
 * per chart is one round trip that carries the tables, the chairs, the
 * attending list and the stale flags, and it would be dead weight on every
 * other tab.
 */
export default function SeatingTab({ group }: { group: GroupDetail }) {
  const queryClient = useQueryClient();
  const [openChartId, setOpenChartId] = useState<string | null>(null);
  const { timeZone } = resolveTripTimeZone(group.timeZone);

  const chartsQuery = useQuery<SeatingChartSummary[]>(
    CHARTS_KEY(group.id),
    () => seatingApi.listCharts(group.id),
    { staleTime: 30_000 },
  );
  // Reused rather than refetched per chart: the dinner picker needs the same
  // holds the Dinners tab already loads under this key.
  const holdsQuery = useQuery<GroupDinnerHold[]>(
    ['group-dinner-holds', group.id],
    () => groupsApi.listDinnerHolds(group.id),
    { staleTime: 30_000 },
  );

  const refreshCharts = () => queryClient.invalidateQueries(CHARTS_KEY(group.id));

  const createMutation = useMutation(
    (body: CreateSeatingChartRequest) => seatingApi.createChart(group.id, body),
    {
      onSuccess: (chart) => {
        refreshCharts();
        // Straight into the room: a chart with no tables is not a thing anyone
        // wanted, it is a step on the way to one.
        setOpenChartId(chart.chart.id);
      },
    },
  );
  const archiveMutation = useMutation(
    (chartId: string) => seatingApi.archiveChart(group.id, chartId),
    { onSuccess: refreshCharts },
  );

  const error = seatingErrorMessage(archiveMutation.error);

  if (openChartId) {
    return (
      <SeatingChartEditor
        groupId={group.id}
        chartId={openChartId}
        timeZone={timeZone}
        onChanged={refreshCharts}
        onBack={() => setOpenChartId(null)}
      />
    );
  }

  if (chartsQuery.isLoading) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 3 }}>
        <CircularProgress size={20} /><Typography color="text.secondary">Loading…</Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {chartsQuery.error ? <Alert severity="error">Couldn't load seating charts.</Alert> : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
      <SeatingChartList
        charts={chartsQuery.data ?? []}
        dinnerHolds={holdsQuery.data ?? []}
        timeZone={timeZone}
        creating={createMutation.isLoading}
        createError={seatingErrorMessage(createMutation.error)}
        onCreate={(body) => createMutation.mutate(body)}
        onOpen={(chartId) => setOpenChartId(chartId)}
        onArchive={(chart) => {
          // Archiving is soft and it frees the dinner for a fresh chart, but the
          // room disappears from the list — worth a beat.
          if (!window.confirm(`Archive “${chart.name || 'this chart'}”? The seating is kept, but it comes off the list.`)) return;
          archiveMutation.mutate(chart.id);
        }}
      />
    </Stack>
  );
}
